// Between the entry the sheet edits and the shape the application form holds.
//
// The form stores dates as Date objects and keeps a few fields the sheet never
// shows (fax, equipment types, the address record). Nothing here invents a
// value: a field the sheet does not collect is carried through untouched, so
// editing an entry cannot quietly drop what a recruiter put on it.

import { unformatPhoneNumber } from 'src/lib/phone.utils'
import { toMonthIndex } from 'src/utils/date/monthPeriods'
import { MONTHS_PER_YEAR } from 'src/utils/date/monthPeriods'

import type { EntryDraft } from './validation'

/** The loosely-typed shape the driver application's form holds. */
export interface FormExperience {
  employmentExperienceId?: string
  companyName?: string | null
  position?: string | null
  reasonForLeaving?: string | null
  unemploymentReason?: string | null
  startDate?: Date | string | null
  endDate?: Date | string | null
  current?: boolean
  isUnemployment?: boolean
  dotNumber?: string | null
  contactName?: string | null
  email?: string | null
  phone?: string | null
  isDriverTerminated?: boolean | null
  isDriverSubjectToFMCSRs?: boolean | null
  isJobDesignatedAsSafetySensitiveFunctionUnderDOT?: boolean | null
  shouldContactEmployer?: boolean | null
  address?: {
    street?: string | null
    city?: string | null
    state?: string | null
    country?: string | null
    zipCode?: string | null
  } | null
  [key: string]: unknown
}

/** A month index back to the first of that month, in UTC. */
export const monthToDate = (monthIndex: number): Date =>
  new Date(
    Date.UTC(
      Math.floor(monthIndex / MONTHS_PER_YEAR),
      ((monthIndex % MONTHS_PER_YEAR) + MONTHS_PER_YEAR) % MONTHS_PER_YEAR,
      1
    )
  )

/**
 * The form writes "" where the sheet means null. Normalising on the way in is
 * what keeps a round trip stable — otherwise an entry opened and saved without
 * a single edit comes back different from how it went in.
 */
const text = (value: string | null | undefined): string | null =>
  value && value.trim() ? value : null

export const toEntryDraft = (experience: FormExperience): EntryDraft => ({
  kind: experience.isUnemployment ? 'unemployment' : 'employment',
  // A DOT number is only ever set by picking from the registry
  carrierSelected: Boolean(experience.dotNumber),
  companyName: text(experience.companyName),
  dotNumber: text(experience.dotNumber),
  city: text(experience.address?.city),
  state: text(experience.address?.state),
  street: text(experience.address?.street),
  zipCode: text(experience.address?.zipCode),
  startMonth: toMonthIndex(experience.startDate),
  endMonth: toMonthIndex(experience.endDate),
  isCurrent: Boolean(experience.current) || experience.endDate == null,
  position: text(experience.position),
  reasonForLeaving: text(experience.reasonForLeaving),
  unemploymentReason: text(experience.unemploymentReason),
  isDriverTerminated: experience.isDriverTerminated ?? null,
  isDriverSubjectToFMCSRs: experience.isDriverSubjectToFMCSRs ?? null,
  isJobDesignatedAsSafetySensitiveFunctionUnderDOT:
    experience.isJobDesignatedAsSafetySensitiveFunctionUnderDOT ?? null,
  shouldContactEmployer: experience.shouldContactEmployer ?? null,
  equipmentTypes: (experience.equipmentTypes as string[]) ?? [],
  contactName: text(experience.contactName),
  phone: text(experience.phone),
  email: text(experience.email),
})

/**
 * Fold an edited entry back into the stored one.
 *
 * `existing` is spread first and only the fields the sheet owns are written
 * over it. Anything else the record carries — equipment types, the fax number,
 * the rest of the address — survives an edit untouched.
 */
export const toFormExperience = (
  draft: EntryDraft,
  existing: FormExperience = {}
): FormExperience => ({
  ...existing,
  isUnemployment: draft.kind === 'unemployment',
  companyName: draft.companyName ?? '',
  dotNumber: draft.dotNumber ?? '',
  position: draft.position ?? '',
  reasonForLeaving: draft.reasonForLeaving ?? '',
  unemploymentReason: draft.unemploymentReason ?? '',
  startDate:
    draft.startMonth == null ? undefined : monthToDate(draft.startMonth),
  // null, not undefined: an ongoing job has no end date, and the form schema
  // and the API both read null as "still there".
  endDate:
    draft.isCurrent || draft.endMonth == null
      ? null
      : monthToDate(draft.endMonth),
  current: Boolean(draft.isCurrent),
  // Still working there entails not having been dismissed from there — but only
  // where the driver has not said otherwise. The question is shown and editable
  // on a current job, so overwriting it here meant an answer could be given,
  // seen, and then silently replaced by the save.
  isDriverTerminated:
    draft.isCurrent && draft.isDriverTerminated == null
      ? false
      : draft.isDriverTerminated ?? null,
  equipmentTypes: draft.equipmentTypes ?? [],
  contactName: draft.contactName ?? '',
  phone: draft.phone ?? '',
  email: draft.email ?? '',
  isDriverSubjectToFMCSRs: draft.isDriverSubjectToFMCSRs ?? null,
  isJobDesignatedAsSafetySensitiveFunctionUnderDOT:
    draft.isJobDesignatedAsSafetySensitiveFunctionUnderDOT ?? null,
  shouldContactEmployer: draft.shouldContactEmployer ?? null,
  // AddressInput requires street, city, state, country and zipCode — all
  // non-null. Sending a partial address rejects the whole mutation, which
  // silently failed every save for a manually entered employer: the sheet
  // closed, the list updated, and nothing reached the server.
  address: {
    ...(existing.address ?? {}),
    street: draft.street ?? existing.address?.street ?? '',
    city: draft.city ?? existing.address?.city ?? '',
    state: draft.state ?? existing.address?.state ?? '',
    country: existing.address?.country ?? 'US',
    zipCode: draft.zipCode ?? existing.address?.zipCode ?? '',
  },
})

/**
 * True for a row that holds nothing a driver typed.
 *
 * The form seeds a blank experience so the old accordion had something to open.
 * It carries an id and no content, and because EmploymentExperienceInput
 * requires startDate, sending it rejects the entire mutation — which is how one
 * untouched placeholder stopped every save on the page.
 */
export const isBlankExperience = (experience: FormExperience): boolean =>
  !experience.startDate &&
  !experience.endDate &&
  !experience.companyName?.trim() &&
  !experience.unemploymentReason?.trim() &&
  !experience.reasonForLeaving?.trim() &&
  !experience.phone?.trim() &&
  !experience.email?.trim() &&
  !experience.contactName?.trim()

/**
 * The payload the application submits, built the same way by whichever screen
 * collected it.
 *
 * `current` is a form-only flag — the API infers an ongoing job from a missing
 * end date — so it is dropped rather than sent. Phone numbers go up as digits.
 *
 * Blank rows are left out. Anything absent from this array is deleted, so this
 * is only safe because a blank row holds nothing to lose.
 *
 * An entry with content but no start date is a different problem: it cannot be
 * sent, because startDate is required, and it cannot be omitted, because that
 * deletes it. So it is kept here and `unsendableEntries` reports it — the save
 * is refused and the driver is told, rather than the mutation failing for
 * reasons nothing on screen explains.
 */
export const toApplicationPayload = (experiences: FormExperience[]) => ({
  employmentExperiences: experiences
    .filter((experience) => !isBlankExperience(experience))
    .map(({ current: _current, ...experience }) => ({
      ...experience,
      phone: unformatPhoneNumber(experience.phone),
      faxNumber: unformatPhoneNumber(experience.faxNumber),
    })),
})

/**
 * Entries that hold something but cannot be submitted.
 *
 * EmploymentExperienceInput requires startDate. One such row makes every save on
 * the page fail, because a save sends the whole array — so the caller has to
 * refuse and say which entry needs dates, instead of letting the server reject
 * a payload the driver cannot see.
 */
export const unsendableEntries = (
  experiences: FormExperience[]
): FormExperience[] =>
  experiences.filter(
    (experience) => !isBlankExperience(experience) && !experience.startDate
  )
